import { useCallback, useEffect, useState } from "react";
import { fetchFastApi } from "../api/fastapi";

import { z } from "zod";
import { toast } from "sonner";
import { extractionResult } from "../models/extraction";
import {
  attributesValidator,
  outputMappingValidator,
} from "../models/schemasV2";

interface useExtractionProps {
  extractionId: string;
  fetchOnLoad?: boolean;
}

const s3SignedURLSchema = z.object({
  url: z.string(),
});

export const useExtraction = ({
  extractionId,
  fetchOnLoad = true,
}: useExtractionProps) => {
  const [loading, setLoading] = useState(fetchOnLoad);
  const [extraction, setExtraction] = useState<z.infer<
    typeof extractionResult
  > | null>(null);
  const [attributes, setAttributes] = useState<
    Record<string, z.infer<typeof attributesValidator>>
  >({});
  const [outputMappings, setOutputMappings] = useState<
    z.infer<typeof outputMappingValidator>[]
  >([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string> | null>(
    null,
  );

  const fetchSignedUrl = useCallback(
    async (fileId: string) => {
      try {
        const data = await fetchFastApi({
          path: `/extractions/extraction/${extractionId}/content/${fileId}`,
          method: "GET",
        });
        const signedUrlData = s3SignedURLSchema.parse(data);
        return signedUrlData.url;
      } catch (error) {
        console.error(`Error fetching signed URL for file ${fileId}:`, error);
        return null;
      }
    },
    [extractionId],
  );

  const fetchExtraction = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFastApi({
        path: `/extractions/extraction/${extractionId}`,
        method: "GET",
      });
      if (data.extraction) {
        const extractionData = extractionResult.parse(data.extraction);
        const attributesData = z
          .record(attributesValidator)
          .parse(data.attributes);
        const outputMappingsData = z
          .array(outputMappingValidator)
          .parse(data.outputMappings);
        setAttributes(attributesData);
        setOutputMappings(outputMappingsData);
        setExtraction(extractionData);
        setLoading(false);

        // Fetch signed URLs for each file
        const fileIds: string[] = extractionData
          .map((doc) => doc.file.fileId)
          .filter(Boolean) as string[];
        const urlPromises = fileIds.map((fileId) => fetchSignedUrl(fileId));
        const urls = await Promise.all(urlPromises);
        const urlMap: Record<string, string> = Object.fromEntries(
          fileIds
            .map((fileId, index) => [fileId!, urls[index]])
            .filter(Boolean),
        );
        if (Object.keys(urlMap).length > 0) {
          setSignedUrls(urlMap);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching extraction");
    } finally {
      setLoading(false);
    }
  }, [extractionId, fetchSignedUrl]);

  useEffect(() => {
    if (fetchOnLoad) {
      fetchExtraction().catch(() => toast.error("Error fetching extraction"));
    }
  }, [fetchOnLoad, fetchExtraction]);

  return { loading, extraction, attributes, outputMappings, signedUrls };
};
